<template>
  <div ref="el" class="search-module-detail-summary form-group">
    <lesson-preview v-if="showLessonPreview" :params="params" :show-info-header="false" />

    <div class="lesson-preview-details">
      <!-- Game Details -->
      <cv-form v-if="showGamification" class="game-details">
        <h5 class="form-header">
          <span>{{ t("search_resource_detail_game_detail_label") }}</span>
        </h5>
        <dl class="field-set">
          <dd class="field-value">
            <SearchResourceDetailsGameBookSection
              :params="params"
              :is-selectable="isGameBookSelectable"
              :is-selected="isGameBookSelected"
              @selected="handleGameBookSelected"
            />
          </dd>
        </dl>
      </cv-form>

      <!-- Location -->
      <cv-form v-if="showLocation" class="location-details">
        <dl class="field-set type-text location">
          <dt class="field-label">{{ t("search_resource_detail_location_label") }}</dt>
          <dd class="field-value">
            <span class="message">
              {{ params.location }}
            </span>
          </dd>
        </dl>
      </cv-form>

      <!-- Course Details -->
      <cv-form class="course-details">
        <h5 class="form-header other-information">
          <span>{{ t("search_resource_detail_other_information_label") }}</span>
        </h5>
        <!-- subject and level -->
        <match-indicator v-if="matchSubjectAndLevel" />
        <dl v-if="showSubjectAndLevel" class="field-set type-text subject">
          <dt class="field-label">{{ t("search_resource_detail_subject_and_level_label") }}</dt>
          <dd class="field-value">
            <data-table-list class="subject-and-level-datatable" in-subpage>
              <!--FIXME VUE3: CURRENTLY NOT SUPPORTED
                mobile-breakpoint="0"
              -->
              <v-data-table
                :items="sortedSubjectLevelTaggings"
                :headers="subjectAndLevelDataTableHeaders"
                :items-per-page-options="subjectAndLevelDataTableFooterProps.itemsPerPageOptions"
                fixed-header
                mobile-breakpoint="0"
              >
                <!-- Subject -->
                <template #[`item.subjectName`]="{ item }">
                  <data-table-cell class="subject">
                    <span v-if="item.subjectName" :title="item.subjectName" class="main">
                      {{ `${item.subjectName} - ${item.subjectKey}` }}
                    </span>
                    <span v-else class="main"></span>
                  </data-table-cell>
                </template>

                <!-- Level -->
                <template #[`item.levelAndStream`]="{ item }">
                  <data-table-cell class="level">
                    <span v-if="item.levelAndStream" :title="item.levelAndStream" class="main">
                      {{ item.levelAndStream }}
                    </span>
                    <span v-else class="main"></span>
                  </data-table-cell>
                </template>
              </v-data-table>
            </data-table-list>
          </dd>
        </dl>
        <!-- contributors -->
        <match-indicator v-if="matchContributors" />
        <dl class="field-set type-text contributors">
          <dt class="field-label">{{ t("search_resource_detail_credited_label") }}</dt>
          <dd class="field-value">
            <div class="output-text">
              <label-component
                kind="creditor"
                :class="{ 'creditor-moe': isMOEResource }"
                :text="contributors"
                @click="handleProfileClick({ userId: contributorsId, isDisabled: isMOEResource })"
              />
            </div>
            <div v-if="vm.showTruncate(1)" class="truncate" />
            <time class="date">{{ createdDate }}</time>
          </dd>
        </dl>
        <!-- modified by -->
        <dl class="field-set type-text modified-by">
          <dt class="field-label">{{ t("search_resource_detail_edited_label") }}</dt>
          <dd class="field-value">
            <label-component
              kind="creditor"
              :text="modifiedBy"
              @click="handleProfileClick({ userId: modifiedById, isDisabled: false })"
            />
            <time class="date">{{ modifiedDate }}</time>
          </dd>
        </dl>
        <!-- credited to -->
        <dl class="field-set type-text creditedTo">
          <dt class="field-label">{{ t("search_resource_detail_credited_to_label") }}</dt>
          <dd class="field-value">
            <div class="creditedToContent" :class="creditedToClass">
              <div v-if="showCreditedTo">
                <span v-for="(tag, index) in creditedTo" :key="index">
                  <label-component
                    :text="tag.username"
                    kind="creditor"
                    @click="handleProfileClick({ userId: tag.userId, isDisabled: false })"
                  />
                </span>
              </div>
              <label-component v-else text="-" class="blank" kind="creditor" />
            </div>
            <cv-link
              v-if="truncateCreditedTo && showCreditedTo"
              class="btn-toggle-truncation"
              @click="handleToggleTruncation"
            >
              <!-- Read More / Read Less -->
              <span class="truncation-toggle">{{ truncateActionLabel }}</span>
            </cv-link>
          </dd>
        </dl>
        <!-- View count -->
        <dl v-if="showViewCount" class="field-set type-text view-count">
          <dt class="field-label">{{ t("search_resource_detail_view_count_label") }}</dt>
          <dd class="field-value">
            <svg-icon name="View24" kind="action" />
            <span>{{ viewCount }}</span>
          </dd>
        </dl>
        <match-indicator v-if="matchKeyword" />
        <!-- Keywords -->
        <dl class="field-set type-text keywords">
          <dt class="field-label">{{ t("search_resource_detail_keywords_label") }}</dt>
          <dd v-if="renderKeyword" class="field-value">
            <Keywords :tags="keywords" />
          </dd>
          <dd v-else class="field-value">
            {{ keywords }}
          </dd>
        </dl>
      </cv-form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import SearchResourceDetailsGameBookSection from "@modules/search/components/SearchResourceDetailsGameBookSection.vue";
import LessonPreview from "@plugins/resource/LessonPreview.vue";
import { isEmpty, notNull } from "@utils/var.check.ts";
import sortUtils from "@utils/sort.utils.ts";
import {
  LOCATION_TYPE,
  NAMESPACE,
  RESOURCE_DOMAIN,
} from "@modules/search/service/search.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import resourceApi from "@apis/resource.api.ts";
import DOMUtils from "@utils/dom.utils.ts";
import SettingsSummaryMixin from "@modules/editor/mixin/settings.summary.mixin.ts";
import MatchIndicator from "@plugins/common/components/MatchIndicator.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import DataTableList from "@plugins/vuetify/components/DataTableList.vue";
import { useSearchProfile } from "@modules/search/composables/use-search-profile.ts";
import { useVm } from "@utils/vm.ts";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import {
  LessonSelectionTypeEnum,
  SearchModuleDetail,
} from "@modules/resource-search/resource-search-types.ts";
import DataTableCell from "@plugins/vuetify/components/DataTableCell.vue";
import Keywords from "@plugins/keywords/components/Keywords.vue";

defineOptions({
  mixins: [SettingsSummaryMixin],
});

interface Props {
  params: SearchModuleDetail;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  gameBookSelected: [id: string | null];
  anchor: [anchorId: string];
}>();

const { t } = useI18n();
const store = useStore();
const { vm } = useVm();
const { handleProfileClick } = useSearchProfile();

const subjectAndLevelDataTableHeaders = ref<unknown[]>([]);
const subjectAndLevelDataTableFooterProps = ref({
  itemsPerPageOptions: [],
});
const creditedTo = ref<unknown[]>([]);
const truncateCreditedTo = ref(true);
const isCreditedToExpanded = ref(false);
const truncationType = ref("primary");
const selectedGameBookId = ref<string | null>(null);
const el = useTemplateRef<HTMLElement>("el");

const isTeacher = computed(() => store.getters[`${NAMESPACE.AUTH}/isTeacher`]);

const showLessonPreview = computed(() => {
  return props.params.lesson
    ? !isEmpty(props.params.lesson.thumbnailUrl) || !isEmpty(props.params.lesson.description)
    : false;
});

const showGamification = computed(() => {
  return props.params.lesson ? props.params.lesson.gameEnabled : false;
});

const isGameBookSelectable = computed(() => {
  return props.params.selectionType === LessonSelectionTypeEnum.gamebook;
});

const isGameBookSelected = computed(() => {
  return notNull(selectedGameBookId.value);
});

const showLocation = computed(() => {
  return props.params.type !== "ASSIGNMENT_LESSON";
});

const matchSubjectAndLevel = computed(() => {
  return isEmpty(props.params.highlights)
    ? null
    : props.params.highlights.subjectMap || props.params.highlights.levelAndStreamMap;
});

const subjectAndLevelList = computed(() => {
  return props.params.lesson.subjectAndLevelList;
});

const showSubjectAndLevel = computed(() => {
  return !isEmpty(subjectAndLevelList.value);
});

const sortedSubjectLevelTaggings = computed(() => {
  if (isEmpty(subjectAndLevelList.value)) return [];

  const sortConfig = [
    { field: "subjectName", asc: true },
    { field: "levelAndStream", asc: true },
  ];

  return sortUtils.sortObjects(subjectAndLevelList.value, sortConfig);
});

const matchContributors = computed(() => {
  return isEmpty(props.params.highlights) ? null : props.params.highlights.createdBy;
});

const isMOEResource = computed(() => {
  return props.params.lesson.resourceDomain === RESOURCE_DOMAIN.MOE;
});

const contributors = computed(() => {
  if (
    props.params.lesson.resourceDomain === LOCATION_TYPE[2].value ||
    props.params.lesson.publisher === LOCATION_TYPE[2].value
  ) {
    return (
      t("search_resource_detail_publisher_label") + ` ${props.params.lesson.ownerGroup?.name ?? ""}`
    );
  } else {
    const createdByName = props.params.lesson.createdByName;
    if (createdByName) {
      return createdByName;
    }
    return "-";
  }
});

const contributorsId = computed(() => {
  if (
    props.params.lesson.resourceDomain === LOCATION_TYPE[2].value ||
    props.params.lesson.publisher === LOCATION_TYPE[2].value
  ) {
    return 0;
  } else {
    const ownerId = props.params.lesson.ownerId;
    if (ownerId) {
      return ownerId;
    }
    return 0;
  }
});

const createdDate = computed(() => {
  return DateTimeFormatter.formatDateTimeToDateTimeAmPm(props.params.lesson.createdDate);
});

const modifiedBy = computed(() => {
  return props.params.lesson.modifiedByName;
});

const modifiedById = computed(() => {
  return props.params.lesson.modifiedById;
});

const modifiedDate = computed(() => {
  return DateTimeFormatter.formatDateTimeToDateTimeAmPm(props.params.lesson.modifiedDate);
});

const creditedToClass = computed(() => {
  return [
    {
      collapsed: showCreditedTo.value && truncateCreditedTo.value && !isCreditedToExpanded.value,
    },
    `${truncationType.value}-truncation`,
  ];
});

const showCreditedTo = computed(() => {
  return creditedTo.value && creditedTo.value.length > 0;
});

const truncateActionLabel = computed(() => {
  return isCreditedToExpanded.value ? t("read_less") : t("read_more");
});

const showViewCount = computed(() => {
  return (
    props.params.lesson.resourceDomain !== RESOURCE_DOMAIN.PERSONAL &&
    props.params.lesson.resourceDomain !== RESOURCE_DOMAIN.ALP_TEMPLATE &&
    props.params.lesson.resourceDomain !== RESOURCE_DOMAIN.ASSIGNMENT
  );
});

const viewCount = computed(() => {
  return props.params.lesson && !isEmpty(props.params.lesson.viewCount)
    ? props.params.lesson.viewCount
    : 0;
});

const matchKeyword = computed(() => {
  return isEmpty(props.params.highlights) ? null : props.params.highlights.keyword;
});

const keywords = computed(() => {
  return props.params.lesson.keywords ? props.params.lesson.keywords : "-";
});

const renderKeyword = computed(() => {
  return keywords.value !== "-";
});

function handleToggleTruncation() {
  isCreditedToExpanded.value = !isCreditedToExpanded.value;
}

function initSubjectAndLevelDataTableOption() {
  subjectAndLevelDataTableHeaders.value = [
    {
      title: t("search_resource_detail_subject_header_label"),
      class: "subject",
      key: "subjectName",
      sortable: false,
    },
    {
      title: t("search_resource_detail_level_header_label"),
      class: "level",
      key: "levelAndStream",
      sortable: false,
    },
  ];

  subjectAndLevelDataTableFooterProps.value = {
    itemsPerPageOptions: [],
  };
}

async function fetchContributors() {
  try {
    const response = await resourceApi.fetchContributors(
      props.params.lesson.uuid,
      props.params.lesson.resourceDomain,
    );
    if (response.code !== "ok") {
      throw response.errorMap;
    }
    if (response.data.resourceContributor.length > 0) {
      creditedTo.value = response.data.resourceContributor.sort((a, b) => a.index - b.index);
    } else {
      creditedTo.value = [];
    }
  } catch (error) {
    console.error(error); // Or handle error appropriately
    throw error;
  }
}

async function determineCreditedToTruncation() {
  if (!showCreditedTo.value) return;
  await nextTick();
  if (el.value) {
    const creditedToContainer = DOMUtils.elOf(el.value, ".creditedToContent")[0];
    if (creditedToContainer.clientHeight < creditedToContainer.scrollHeight) {
      isCreditedToExpanded.value = false;
    } else {
      truncateCreditedTo.value = false;
    }
  }
}

function handleGameBookSelected(isSelected: boolean) {
  const gameBookId = props.params.lesson?.gameBookModel?.id;
  selectedGameBookId.value = isSelected && gameBookId ? String(gameBookId) : null;
  emit("gameBookSelected", selectedGameBookId.value);
}

function initSelectedGameBook() {
  selectedGameBookId.value = String(props.params.lesson?.gameBookModel?.id) ?? null;
  if (selectedGameBookId.value) {
    handleGameBookSelected(true);
  }
}

onMounted(async () => {
  initSubjectAndLevelDataTableOption();
  if (!isMOEResource.value && isTeacher.value) {
    await fetchContributors();
    await determineCreditedToTruncation();
  }
  initSelectedGameBook();
});
</script>

<style lang="scss">
@use "@styles/layout/z-map" as *;
@use "@styles/abstracts/mixins" as *;
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;
@use "@styles/themes/sls-theme/tokens/typo" as *;

.search-module-detail-summary {
  @include media("desktop") {
    display: flex;
    gap: $spacing-07;

    :is(.lesson-preview-info, .lesson-preview-details) {
      flex: 1;
    }
  }

  &.show-notes {
    .additional-information {
      display: block;
    }
  }

  .bx--form {
    &:first-of-type {
      margin-top: 0;
    }

    &.info-content {
      .field-value {
        margin-top: 0;
      }
    }
  }

  .lesson-preview-info {
    &:empty,
    &:has(.info-content:empty) {
      display: none;
    }
  }

  .course-details {
    .form-header {
      &.other-information {
        margin-bottom: $spacing-05;
      }
    }

    .field-value {
      // set a smaller value margin-top to minus off margin added in label-component
      margin-top: $spacing-02;

      .subject-and-level-datatable {
        .v-table__wrapper {
          thead {
            tr {
              th {
                width: 50%;
              }
            }
          }

          tbody {
            tr {
              td {
                white-space: normal;

                .table-cell-wrapper {
                  > span.main {
                    display: block;
                  }
                }
              }
            }
          }
        }
      }

      .label-component {
        span {
          @include carbon--type-style("label-01", true);
          @include line-clamp(1);
          min-width: 2rem;
          color: $text-01;

          // to overwrite token line-height
          // using line-height instead of padding to retain pill height 32px
          // and prevent text wrap seen as line-clamp is added
          line-height: 1.5rem;
          @include media("desktop") {
            line-height: 1.5rem;
          }

          // align text to center for empty tags
          &[title="-"] {
            text-align: center;
          }
        }
      }

      .keywords {
        .bx--tag {
          padding: 0.375rem $spacing-03; // to increase pill height to 32px
        }
      }
    }
  }

  .additional-information {
    @include carbon--type-style("body-long-01");
    display: none;
    width: 100%;
    margin-top: $spacing-05;
    padding: $spacing-05;
    color: $text-01;
    background-color: $ui-background;
    border-radius: 10px;

    .empty-placeholder {
      margin: 0;

      .content {
        margin-bottom: 0;
      }

      .placeholder-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 80px;
        margin: 0;
      }

      svg {
        transform: scale(0.5);
      }
    }

    label {
      @include carbon--type-style("panel-title-01");
      display: flex;
      align-items: center;
      margin-bottom: $spacing-05;
      color: $text-02;

      .sls-icon {
        margin-right: $spacing-03;
      }
    }
  }

  .field-set {
    &.modified-by {
      .field-value {
        display: flex;
        align-items: center;
        flex-flow: row wrap;

        .date {
          margin-top: $spacing-02;
          flex: 0 0 100%;
        }
      }

      .creditor {
        width: auto;
        margin-right: $spacing-03;
      }
    }

    &.creditedTo {
      .creditedToContent {
        position: relative;
        word-break: break-word;

        &.collapsed {
          max-height: 4rem;
          overflow: hidden;

          > span {
            cursor: pointer;
          }

          &::before {
            content: "";
            position: absolute;
            height: 50%;
            width: 100%;
            bottom: 0;
            background-image: linear-gradient(rgba(255, 255, 255, 0) 50%, rgba($ui-01, 1));
            z-index: z-map("default");
          }
        }
      }

      .btn-toggle-truncation {
        @include carbon--type-style("card-short-01");
        margin-top: $spacing-01;
      }
    }

    &.view-count {
      .field-value {
        display: flex;
        align-items: center;
        gap: $spacing-03;
      }
    }
  }

  .notes-toggle {
    label {
      display: flex;
      flex-flow: row-reverse;
      align-items: center;
      gap: $spacing-02;

      .bx--toggle__switch {
        margin-top: $spacing-05;
      }
    }
  }

  .section-activity-list {
    .selection-bar {
      &._active {
        border-radius: 10px;
      }
    }

    .v-table__wrapper {
      .v-data-table-header {
        span {
          @include carbon--type-style("productive-heading-01");
        }
      }

      table {
        td {
          left: 0 !important;

          .table-cell-wrapper {
            > span {
              max-width: unset;
            }

            &.title {
              padding: $spacing-05;
              flex-flow: column wrap;
              align-items: flex-start;

              &.simple-view {
                .label-component,
                .notes {
                  display: none;
                }
              }

              &.has-input-box {
                .activity-checkbox {
                  margin-right: $spacing-03;
                }

                .activity-title-wrapper {
                  max-width: calc(100% - 7rem);
                }
              }

              > .activity-header-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
              }

              .activity-title-wrapper {
                display: flex;
                flex-flow: row wrap;
                max-width: calc(100% - 4rem);

                .label-component {
                  max-width: calc(100% - 1rem);

                  > .ellipsis-text {
                    overflow: hidden;
                  }
                }
              }

              // Intended specificity to override generic styling
              &:not(.checkbox):not(.action) {
                margin-right: 0;

                @include media("tablet-max") {
                  .page-title {
                    align-self: center;
                    overflow: hidden;
                  }
                }
              }
            }
          }
        }
      }

      th {
        background-color: $ui-04;

        .all-checkbox {
          display: inline-flex;
          vertical-align: middle;
          margin-right: $spacing-03;
        }
      }
    }

    .bx--link {
      &:hover {
        color: $link-01;
        text-decoration: none;
      }
    }

    .view-ext {
      margin-left: auto;
      min-width: 16px;
      cursor: pointer;

      [stroke] {
        stroke: $interactive-01;
      }

      [fill] {
        fill: $interactive-01;
      }
    }

    .grouper {
      &.disabled {
        // For section-grouper's border, activity-grouper doesn't have
        border-top-color: $disabled-02;

        > div {
          pointer-events: none;
        }

        .bx--link {
          color: $text-03;
        }

        .icon {
          path {
            &[fill] {
              fill: $disabled-02;
            }

            &[stroke] {
              stroke: $disabled-02;
            }
          }
        }

        .page-title {
          color: $text-03;
        }

        .view-ext {
          [fill] {
            fill: $disabled-02;
          }

          [stroke] {
            stroke: $disabled-02;
          }
        }
      }
    }

    .activity-grouper {
      margin-top: $spacing-05;

      &.disabled {
        .page-label {
          color: $text-03;
        }
      }

      .v-table {
        .v-table__wrapper {
          border-radius: 10px;

          table {
            > tbody {
              > tr {
                &:last-child {
                  td {
                    &:first-child {
                      border-bottom-left-radius: 10px;
                    }

                    &:last-child {
                      border-bottom-right-radius: 10px;
                    }
                  }
                }

                background-color: $ui-background;

                > td {
                  height: 100%;
                  background-color: $ui-background;

                  &.v-data-table__td {
                    &:nth-child(2) {
                      max-width: 300px;
                    }
                  }
                }
              }
            }
          }
        }
      }

      .additional-information {
        background-color: $ui-01;
      }
    }

    .section-grouper {
      position: relative;
      padding: $spacing-05;
      margin: $spacing-05 0;
      background-color: $ui-background;
      border: 1px solid $ui-04;
      border-top: 4px solid var(--color);
      border-radius: 10px;

      &.is-section-hidden {
        .icon {
          opacity: 0.5;
        }
      }

      .match-indicator {
        margin-bottom: -($spacing-05);
        padding: $spacing-05 0 $spacing-03 $spacing-05;
      }
    }

    .section-component {
      background-color: $ui-01;
      border-radius: 10px;

      .wrapper {
        display: grid;
        grid-template-columns: 2.5rem auto auto;
        align-items: center;
        padding: $spacing-05;

        &.has-input-box {
          grid-template-columns: 2rem 2.5rem auto auto;
        }

        .additional-information {
          grid-column: 1 / -1;
        }
      }

      .page-label {
        color: $ui-01;
      }
    }

    .section-info {
      display: flex;
      align-items: center;
      padding-top: $spacing-03;

      .actions-buttons {
        @include carbon--type-style("card-short-01", true);
      }
    }

    .section-activities {
      margin-top: $spacing-03;
      background-color: $ui-01;
      border-radius: 10px;

      > .v-table {
        background-color: transparent;

        > .v-table__wrapper {
          border-radius: 10px;
        }

        tbody {
          > tr {
            > td {
              height: 100%;
              border: none;
              background-color: $ui-01;

              &.v-data-table__td {
                &:nth-child(2) {
                  max-width: 300px;
                }
              }
            }
          }
        }
      }
    }

    .page-title {
      @include carbon--type-style("panel-title-01", true);
      @include line-clamp(2);
      width: 100%;
      padding-right: $spacing-05;
      color: $text-02;
    }

    .icon {
      display: flex;
      flex: 0 0 auto;
      position: relative;
      margin-right: $spacing-03;

      .sls-icon {
        &[name="SectionHidden32"] {
          & ~ .page-label {
            display: none;
          }
        }
      }
    }

    .page-label {
      @include carbon--type-style("helper-text-01", true);
      position: absolute;
      top: 0.7rem;
      left: 50%;
      transform: translateX(-50%);
      color: $icon-01;
    }
  }

  .passcode-info {
    display: flex;
    margin: $spacing-05 0;

    &:first-child {
      margin-top: 0;
    }

    span {
      @include carbon--type-style("helper-text-01", true);
      margin-left: $spacing-03;
      color: $text-02;
    }
  }
}
</style>
